import React, { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { GrFormNextLink, GrFormPreviousLink } from "react-icons/gr";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import { useAuth } from "../../context/AuthContext";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from "../../helpers/Toast";
import { ValidDebtHourDTO } from "../../models/Assistance/ValidDebtHourDTO";
import { AssistanceExecutionDTO } from "../../models/AssistanceExecution/AssistanceExecutionDTO";
import { DeleteExecutionDTO } from "../../models/AssistanceExecution/DeleteExecutionDTO";
import { GetExecutionsByAssistanceDTO } from "../../models/AssistanceExecution/GetExecutionsByAssistanceDTO";
import { AssistanceService } from "../../services/AssistanceService";
import ButtonComponent from "../ButtonComponent";
import InputComponent from "../InputComponent";
import ModalComponent from "../ModalComponent";
import ExecutionsDebtHoursTable from "./ExecutionsDebtHoursTable";

type AssistanceExecutionsComponentType = {
	assistanceId: number;
	reloadExecutions: boolean;
	mode: "workshop" | "backoffice";
	isComplete: boolean;

	onFetchExecutionsData: () => void;
};

export function ExecutionsTableComponent({ assistanceId, mode, reloadExecutions, isComplete, ...props }: AssistanceExecutionsComponentType) {
	const intl = useIntl();
	const assistanceService = new AssistanceService();
	const auth = useAuth();

	const { availableModules, counterOptions } = useAvailableModulesContext();
	const isActiveCountersModule = availableModules.includes("IsActiveCountersModule");
	const isActiveErpSupportModule = availableModules.includes("IsActiveErpSupportModule");
	const isActiveWorkDoneAndPendingInExecutions = availableModules.includes("IsActiveWorkDoneAndPendingInExecutions");
	const technicianCanDebitHoursInExecutions = availableModules.includes("TechnicianCanDebitHoursInExecutions");

	const [executions, setExecutions] = useState<AssistanceExecutionDTO[]>([]);
	const pageSizes: number[] = [5, 15, 30];
	const [pageSize, setPageSize] = useState<number>(pageSizes[0]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [canPreviousPage, setCanPreviousPage] = useState<boolean>(false);
	const [canNextPage, setCanNextPage] = useState<boolean>(false);
	const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
	const [isLoadingEliminate, setIsLoadingEliminate] = useState<boolean>(false);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [idDeleted, setIdDeleted] = useState<number>(0);
	const [dateAndTecText, SetDateAndTecDate] = useState<string>("");
	const [totalDuration, setTotalDuration] = useState<number>(0);
	const [debtHours, setDebtHours] = useState<number>(0);
	const [isSync, setIsSync] = useState<boolean>(false);
	const [reload, setReload] = useState<boolean>(false);
	const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});
	const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

	const toggleRow = (id: number) => {
		setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const loadExecutions = async () => {
		setIsLoadingTable(true);

		const params: GetExecutionsByAssistanceDTO = {
			assistanceId: assistanceId,
			currentPage: currentPage,
			pageSize: pageSize,
		};

		var response = await assistanceService.GetExecutions(params);

		setIsLoadingTable(false);

		if (response.success === true) {
			setExecutions(response.items);
			setTotalDuration(response.totalDurationExecution);
			setTotalPages(response.totalPages);
			setCanNextPage(response.totalPages > currentPage);
			setCanPreviousPage(currentPage > 1);

			//Se a pesquisa tiver retornado múltiplas páginas e a página atual
			//for superior ao nº de páginas que chegaram mandamos o user para a página 1
			if (response.totalPages > 0 && currentPage > response.totalPages) {
				setCurrentPage(1);
			}
		}
	};

	const previousPage = () => {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1);
		}
	};

	const nextPage = () => {
		if (currentPage + 1 <= totalPages) {
			setCurrentPage(currentPage + 1);
		}
	};

	const deleteExecution = async (executionId: number) => {
		const params: DeleteExecutionDTO = {
			executionId: executionId,
		};

		setIsLoadingEliminate(true);
		var response = await assistanceService.DeleteExecution(params);
		setIsLoadingEliminate(false);

		if (response.success !== true || response.obj == null) {
			Toast.Show("error", response.message);
			return;
		}
		Toast.Show("success", "Execução removida com sucesso");
		loadExecutions();
	};

	const InsertDebtHours = async () => {
		var assistance = await assistanceService.GetById(assistanceId);
		if (assistance.success === false || assistance.obj === null) {
			Toast.Show("error", assistance.message);
			return;
		}
		var sendData: ValidDebtHourDTO = {
			numDoc: assistance.obj.erpNumDoc !== null ? assistance.obj.erpNumDoc : 0,
			serie: assistance.obj.erpSerie !== null ? assistance.obj.erpSerie : "",
			typeDoc: assistance.obj.erpDocType !== null ? assistance.obj.erpDocType : "",
			debtHour: debtHours,
		};
		setIsButtonLoading(true);
		var response = await assistanceService.InsertDebtHours(sendData);
		setIsButtonLoading(false);

		if (response.success === false || response.obj === null) {
			Toast.Show("error", response.message);
			return;
		}
		Toast.Show("success", response.message);
		setReload(!reload);
	};

	useEffect(() => {
		if (assistanceId !== 0) {
			loadExecutions();
		}
	}, [assistanceId, currentPage, pageSize, reloadExecutions]);

	return (
		<>
			<ModalComponent
				IsOpen={isOpen}
				text={`Tem a certeza que quer remover a execução do técnico ${dateAndTecText}`}
				OnClickRight={() => setIsOpen(false)}
				OnClickLeft={() => {
					setIsOpen(false);
					deleteExecution(idDeleted);
				}}
				title={"Remover Execução"}
				buttonRight={"Não"}
				buttonLeft={"Sim"}
			/>

			<Row style={{ margin: "1rem 0px" }}>
				<h5>
					<FormattedMessage id={"Screens.Assistances.Executions.Table.Title"}></FormattedMessage>
				</h5>

				<Row style={{ margin: "1rem 0px" }}>
					<table>
						<thead>
							<tr>
								<th>
									<FormattedMessage id={"Screens.Assistances.Executions.Table.Technician"}></FormattedMessage>
								</th>
								<th>
									<FormattedMessage id={"Screens.Assistances.Executions.Table.StartDate"}></FormattedMessage>
								</th>
								<th>
									<FormattedMessage id={"Screens.Assistances.Executions.Table.EndDate"}></FormattedMessage>
								</th>
								<th>
									<FormattedMessage id={"Screens.Assistances.Executions.Table.Duration"}></FormattedMessage>
								</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{executions.map((m) => {
								const canRemove = (mode === 'backoffice') || (mode === 'workshop' && m.technicianName === auth?.currentUser?.username);
								const isExpanded = expandedRows[m.id];

								return (
									<React.Fragment key={m.id}>
										<tr>
											<td width={250}>
												<label>{m.technicianName}</label>
											</td>

											<td width={250}>
												<label>{m.executionStart}</label>
											</td>

											<td width={250}>
												<label>{m.executionEnd}</label>
											</td>
											<td width={250}>
												<label>{m.durationHours}</label>
											</td>
											<td width={100}>
												<div style={{ display: "inline-flex", height: "30px" }}>
													{isActiveWorkDoneAndPendingInExecutions &&
														<ButtonComponent
															text={isExpanded ? intl.formatMessage({ id: "ExecutionsTableComponent.Close" }) : intl.formatMessage({ id: "ExecutionsTableComponent.Open" })}
															onClick={() => toggleRow(m.id)}
														/>
													}
													<ButtonComponent
														text={intl.formatMessage({ id: "ExecutionsTableComponent.Remove" })}
														icon={<FiX />}
														disabled={!canRemove || (isComplete === false ? isLoadingTable : true)}
														loading={isLoadingEliminate}
														onClick={() => {
															setIdDeleted(m.id);
															SetDateAndTecDate(
																m.technicianName +
																" com a data " +
																m.executionStart +
																" - " +
																m.executionEnd
															);
															setIsOpen(true);
														}}
													/>
												</div>
											</td>
										</tr>
										{isExpanded && (
											<tr>
												<td colSpan={5}>
													<div style={{ display: "flex", flexDirection: "column" }}>
														<div style={{ display: "flex" }}>
															<div style={{ width: "50%", padding: "0.5rem", textAlign: "left" }}>
																<p style={{ marginBottom: "8px" }}><strong>Trabalho Realizado:</strong></p>
																{m.workDone?.trim() ? m.workDone : "Não preenchido"}
															</div>
															<div style={{ width: "50%", padding: "0.5rem", textAlign: "left" }}>
																<p style={{ marginBottom: "8px" }}><strong>Traballho Pendente:</strong></p>
																{m.pending?.trim() ? m.pending : "Não preenchido"}
															</div>
														</div>

														{/* Display counters when both modules are active and counters data exists */}
														{isActiveCountersModule && isActiveErpSupportModule && m.counters && (
															<div style={{ textAlign: "left", borderTop: "1px solid #eee", marginTop: "15px", paddingTop: "15px" }}>
																<label style={{ margin: "0px 0px 15px 0px", padding: "5px 0px", fontWeight: "bold", display: "block", fontSize: "15px" }}>
																	{intl.formatMessage({ id: "Screens.AssistanceEditB.CountersTitle" })}
																</label>
																<Row style={{ margin: "0px", padding: "0px" }}>
																	{counterOptions.map((option, index) => {
																		const counterKey = `counter${index + 1}` as keyof typeof m.counters;
																		const unitKey = `unit${index + 1}` as keyof typeof m.counters;

																		// Split option to get label and unit if available
																		const [label, unit] = option.split(';');

																		// Only display if the counter has a value
																		if (m.counters && m.counters[counterKey] !== undefined) {
																			return (
																				<>
																					<InputComponent
																						xl={3}
																						lg={3}
																						md={3}
																						sm={10}
																						xs={10}
																						key={`counter-${index}`}
																						style={{ width: "100%" }}
																						type={"text"}
																						value={m.counters[counterKey]?.toString()}
																						label={label}
																						readOnly={true}
																					/>
																					<InputComponent
																						xl={1}
																						lg={1}
																						md={1}
																						sm={2}
																						xs={2}
																						key={`unit-${index}`}
																						style={{ width: "100%" }}
																						type={"text"}
																						value={(m.counters[unitKey] || unit || '').toString()}
																						label={"Unidade"}
																						readOnly={true}
																					/>
																				</>
																			);
																		}
																		return null;
																	})}
																</Row>
															</div>
														)}
													</div>
												</td>
											</tr>
										)}
									</React.Fragment>
								);
							})}
						</tbody>
					</table>
				</Row>

				<Row style={{ margin: "1rem 0px" }}>
					<div className="pagination" style={{ opacity: isLoadingTable === true ? 0.5 : 1 }}>
						<button className="paginationButton" onClick={() => previousPage()} disabled={!canPreviousPage || isLoadingTable}>
							<GrFormPreviousLink size={20} />
						</button>

						<span className="paginationNumberIndicator">
							<FormattedMessage id="Common.Labels.Page" /> <strong>{currentPage}</strong>{" "}
							<FormattedMessage id="Common.Labels.Of" /> <strong>{totalPages}</strong>
						</span>

						<button className="paginationButton" onClick={() => nextPage()} disabled={!canNextPage || isLoadingTable}>
							<GrFormNextLink size={20} />
						</button>

						<div className="paginationRecordSize">
							<span>
								<FormattedMessage id="Common.Labels.RecordsPerPage" />
							</span>
							<select
								className="paginationRecordSizeSelect"
								disabled={isLoadingTable}
								value={pageSize}
								onChange={(e) => {
									setPageSize(Number(e.target.value));
								}}
							>
								{pageSizes.map((pageSize) => {
									return (
										<option key={pageSize} value={pageSize}>
											{pageSize}
										</option>
									);
								})}
							</select>
						</div>
					</div>
				</Row>
			</Row>
			<Row style={{ margin: "1rem 0px", marginTop: "4.5%" }}>
				<InputComponent
					xl={4}
					lg={4}
					md={4}
					sm={12}
					xs={12}
					style={{ width: "100%" }}
					type={"number"}
					value={totalDuration}
					label={intl.formatMessage({ id: "Screens.Assistances.Executions.Label.RegistedHours" })}
					readOnly={true}
				/>
				{mode === "backoffice" || (mode === "workshop" && technicianCanDebitHoursInExecutions) ? (
					<>
						<InputComponent
							xl={4}
							lg={4}
							md={12}
							sm={12}
							xs={12}
							style={{ width: "100%" }}
							type={"number"}
							value={debtHours}
							label={intl.formatMessage({ id: "Screens.Assistances.Executions.Label.HoursToDebit" })}
							readOnly={isComplete}
							onChange={(t: number) => {
								setDebtHours(t);
								setIsSync(false);
							}}
						/>
						<Col xl={4} lg={4} md={4} sm={4} xs={12}>
							<ButtonComponent
								loading={isButtonLoading}
								style={{ width: "100%", marginTop: "30px" }}
								text={intl.formatMessage({ id: "ExecutionsTableComponent.ValidateDebtHours" })}
								onClick={() => InsertDebtHours()}
								disabled={isComplete}
							/>
							{isSync === true ? <span style={{ fontSize: "9px" }}>(Informação já sincronizada)</span> : ""}
						</Col>
					</>
				) : (
					""
				)}
			</Row>
			{mode === "backoffice" || (mode === "workshop" && technicianCanDebitHoursInExecutions) ? (
				<Row style={{ margin: "1rem 0px" }}>
					<ExecutionsDebtHoursTable assistanceId={assistanceId} reload={reload} />
				</Row>
			) : (
				""
			)}
		</>
	);
}
